import { useBoundContext } from "lib/@components/binding/use-bound-context"
import {
    Box,
    Button,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import Iconify from "minimals-template/components/Iconify"
import { confirm } from "lib/@dialogs/confirm"
import {
    deleteRecyclerItem,
    restoreRecyclerItem,
} from "routes/schedule/plugins/recycle-bin/controller/recycle-bin-controller"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { NameDialog } from "lib/@dialogs/nameDialog"
import { useDialog } from "lib/@hooks/useDialog"
import { getClientGroups } from "routes/schedule/my-schedules/my-custom-schedules/controller/get-client-trees"
import { useDemand } from "lib/authorization/use-demand"
import { If } from "lib/@components/switch"

export function BinItem() {
    const { target } = useBoundContext()
    const dateDeleted = Date.create(target.date).format(DATE_FORMATS.medium)
    const isClientAdmin = useDemand("clientAdmin")
    // Scenario items keep their existing manageScenarios-gated delete; everything
    // else needs clientAdmin on top of the view baseline (FIQ-1734)
    const canPermanentlyDelete = target.type === "scenario" || isClientAdmin

    const promptRename = useDialog(
        <NameDialog
            title="A Regime with this name already exists"
            placeholder="Enter a new name to restore..."
            label="Maintenance Regime Name"
        />
    )

    return (
        <ListItem className="recycler-item" data-cy="recycler-item">
            <ListItemBox>
                <ListItemIcon data-cy="recycle-bin-item-icon">
                    <Iconify icon={target.icon} />
                </ListItemIcon>
                <ListItemText
                    sx={{ flex: 1, wordBreak: "break-word" }}
                    primary={target.name}
                    secondary={dateDeleted}
                    secondaryTypographyProps={{ "data-cy": "recycle-bin-item-date-deleted" }}
                />
                <Box mr={2}>
                    <Typography variant="overline" color="text.primary" data-cy="recycle-bin-item-type">
                        {target.viewType}
                    </Typography>
                </Box>
                <Box>
                    <Stack direction="row" spacing={1}>
                        <If value={canPermanentlyDelete}>
                            <Button
                                onClick={remove}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                data-cy="recycle-bin-item-delete-button"
                            >
                                Delete
                            </Button>
                        </If>
                        <Tooltip title="Restore">
                            <IconButton
                                className="restore"
                                color="primary"
                                onClick={restore}
                                data-cy="recycle-bin-item-restore-button"
                            >
                                <Iconify icon="fluent-mdl2:remove-from-trash" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </ListItemBox>
        </ListItem>
    )

    async function remove() {
        if (!(await confirm("Are you sure you want to permanently remove this item?", "Permanent Delete"))) return
        try {
            await deleteRecyclerItem(target._id)
            successSnackbar("Item successfully deleted", { key: target._id })
        } catch {
            errorSnackbar("Item could not be deleted")
        }
    }

    async function restore() {
        try {
            let { name } = target
            if (isRegimeItem(target)) {
                const regimes = await getClientGroups()
                const existingRegimeName = regimes.map(toRegimeName).filter(Boolean)

                if (existingRegimeName.some((existingName) => existingName.toLowerCase() === name.toLowerCase())) {
                    const rename = await promptRename({
                        value: name,
                        namesList: existingRegimeName,
                    })
                    if (!rename) return
                    name = rename
                }
            }

            await restoreRecyclerItem({ id: target._id, name: name !== target.name ? name : undefined })
            const snackBarMessage = getRestoreMessage(target, name)

            successSnackbar(snackBarMessage, { key: target._id })
        } catch (e) {
            if (e.message !== "Not found") {
                errorSnackbar("Item could not be restored")
            }
        }
    }

    function toRegimeName(regime) {
        return regime.name
    }
}

function isRegimeItem(item) {
    return item.viewType === "Regime"
}

function getRestoreMessage(target, name) {
    if (isRegimeItem(target) && name !== target.name) {
        return `Regime restored as ${name}`
    }
    return "Item successfully restored"
}
